import { action, observable } from 'mobx';
import stores from '../../models/Stores';
import { esriProjection } from 'cd-eft/utils/esriTypes';
import configureRefererUrlTokensAsync from 'cd-eft/security/esri/configureRefererUrlTokensAsync';
import { UserIdentity } from 'cd-eft/security/UserIdentity';
import * as PRJAPI from 'cd-eft/services/eftPrjAdminApi';
import * as esriTypes from 'cd-eft/utils/esriTypes';
import MessageCenter from '../common/MessageCenter/MessageCenterVM';
import { FlagDto } from 'cd-eft/_codegen_/openapi-generator/eft-prjadmin';
import axios, { AxiosResponse } from 'axios';
import { IApplicationFlags } from 'cd-eft/models/Stores/UserProfileStore';
import { getFpacFeatureFlags } from 'cd-eft/services/fpacFfsApi';
import { appConfig } from 'cd-eft/config/AppConfig';

const AppStore = stores.AppStore;

export class AppVM {
  @observable modelInitialized: boolean = false;
  // @observable basicDataFormValues: any = {};
  @observable sidebarActive = true;

  constructor() { }

  async initializeModel() {
    // console.log('App initialize model called',);
    if (AppStore.appConfig.useEAuth) this.parseUrl();

    // load AMD modules
    // await esriTypes.loadEsriLoaderTypes();
    if (!UserIdentity.expireHandler) {
      UserIdentity.addExpireHandler(this.expireHandler);
    }

    // pre-load the esriProjection module
    await esriProjection.load();

    // initialize esri IdentityManager and pre-register long-lived tokens based on the current url referer location
    await configureRefererUrlTokensAsync(String(window.location)); //begin the IdentityManager setup

    //refresh the user login session
    if (UserIdentity.username) {
      try {
        await UserIdentity.refresh();
      } catch {
        localStorage.clear(); // Possibly redundant check for null. May get caught in API call instead
        AppStore.userProfileStore.userAvailable = false;
        AppStore.userProfileStore.authError = false;
      }

      //if session is invalid, then force logout to clear saved tokens
      if (!UserIdentity.username || !UserIdentity.accessToken || UserIdentity.expired) {
        await UserIdentity.logout();
        localStorage.clear(); // Possibly redundant check for null. May get caught in API call instead
        AppStore.userProfileStore.userAvailable = false;
        AppStore.userProfileStore.authError = false;
      }
    }

    // if login session is valid
    if (UserIdentity.username && UserIdentity.accessToken && !UserIdentity.expired) {
      try {
        const response = (await PRJAPI.UserPreferenceController.getAllUserPreferencesUsingGET(UserIdentity.username!))
          .data;

        if (response.length) {
          AppStore.userProfileStore.userPreferences = response as any;
          AppStore.userProfileStore.lastSavedUserPreferencesJSON = JSON.stringify(
            AppStore.userProfileStore.userPreferences
          );
        }
        if (AppStore.appConfig.useEAuth && !(AppStore.appConfig.useAtun && UserIdentity.username === AppStore.appConfig.atun)) {
          await this.getFpacFlagByEnv();
        }
        else {
          await this.getAllFlags();
        }
      } catch (error) {
        // treat failure to load preferences as non-critical error
        console.error('Failed to load user preferences.');
        console.error(error);
      }
    }

    //start the user preference watch and save routine
    this._watchUserPreference();

    this.modelInitialized = true;
  }

  // Used ONLY in fpac env for parsing tokens/uname from url in redirect
  async parseUrl() {
    if (!AppStore.appConfig.useEAuth) return;
    const queryParams = new URLSearchParams(window.location.search).entries();
    let next1 = queryParams.next();
    let userRoleId: any = 0;
    while (!next1.done) {
      const lowerCase = next1.value[0].toLowerCase();
      if (lowerCase === 'useremail' || lowerCase === 'username') {
        // If email comes back as default CERT email, clear localStorage then redirect to sharepoint page 
        if (next1.value[1] === 'EEMSCERT@ftc.usda.gov') {
          localStorage.clear();
          window.open('https://usdagcc.sharepoint.com/sites/ICAM/SitePages/Request-Test-Accounts.aspx', '_self', "noopener");
        }
        localStorage.setItem('username', next1.value[1]);
      }
      else if (lowerCase === 'accesstoken') {
        localStorage.setItem('accessToken', next1.value[1]);
      }
      else if (lowerCase === 'refreshtoken') {
        localStorage.setItem('refreshToken', next1.value[1]);
      }
      else if (lowerCase === 'systemadminroleid') {
        userRoleId = next1.value[1];
      }
      else {
        localStorage.setItem(next1.value[0], next1.value[1]);
      }
      next1 = queryParams.next();
    }
    await UserIdentity.login(userRoleId, localStorage['accessToken']);
  }

  // @action.bound sideNavCallback(section: any, sidebarActive: boolean) {
  //   if (sidebarActive !== undefined) {
  //     this.sidebarActive = sidebarActive;
  //   }
  // }

  // // collect CN and area from RCN and pass to basic data table
  // @action.bound rcnCallback(CN, area) {
  //   // this.basicDataFormValues = {
  //   //   runoffCN: CN,
  //   //   drainageArea: area,
  //   // };
  // }

  // checks if user preferences have changed at a specified interval
  // interval will stop if blur event is triggered

  private _watchUserPreferenceInterval = 15000;
  private _watchUserPreferenceTimer;

  private _watchUserPreference(): void {
    this._watchUserPreferenceTimer = setInterval(this._checkAndUpdate.bind(this), this._watchUserPreferenceInterval);
    window.onblur = this._windowBlurHandler.bind(this);
    window.onfocus = this._windowFocusHandler.bind(this);
  }
  private _windowBlurHandler(): void {
    if (this._watchUserPreferenceTimer) clearInterval(this._watchUserPreferenceTimer);
    this._checkAndUpdate();
  }
  private _windowFocusHandler(): void {
    if (this._watchUserPreferenceTimer) clearInterval(this._watchUserPreferenceTimer);
    this._watchUserPreferenceTimer = setInterval(this._checkAndUpdate.bind(this), this._watchUserPreferenceInterval);
  }

  private async _checkAndUpdate(): Promise<void> {
    const userPreferences = AppStore.userProfileStore.userPreferences;
    const lastSavedUserPreferencesJSON = AppStore.userProfileStore.lastSavedUserPreferencesJSON;
    const currentProject = AppStore.efh2Store.projectsStore.currentProject;
    // const userName = localStorage.getItem('username');
    const userName = UserIdentity.username;
    // simple comparison of json strings for saved vs current preferences
    if (userName && userPreferences && JSON.stringify(userPreferences) !== lastSavedUserPreferencesJSON) {
      // Have to do this before the update because of the uncaught promise
      AppStore.userProfileStore.lastSavedUserPreferencesJSON = JSON.stringify(userPreferences);
      // loop here and save if preferences found
      for (let i = 0; i < userPreferences.length; i++) {
        if (userPreferences[i].value) {
          // await this.userPreference.updateUserPreference(userName, userPreferences[i], currentProject);
          // console.log('--SAVE USER PREFERENCE--');
          const preference = userPreferences[i];
          await PRJAPI.UserPreferenceController.saveUserPreferenceUsingPOST(userName, preference.key, preference);
        }
      }
    }
  }

  // Callback handed to UserIdentityProvider for use with the auth interceptor
  // Will set store variables here to ensure the page gets changed to login.
  // Will also clear the message center so page specific messages don't reappear when logging back in. could change if error message is displayed when tokens invalid.
  expireHandler(): void {
    // console.log('EXPIRE HANDLER DEFINITION IN APP');
    MessageCenter.clearMessages();
    AppStore.userProfileStore.userAvailable = false;
    AppStore.userProfileStore.authError = false;
  }

  private async getFpacFlagByEnv() {
    try {
      const response = await getFpacFeatureFlags();
      if (response && response.data && response.data.result) {
        const reducedValues = response.data.result.reduce(
          (obj, item) => {
            item.keyName = item.keyName?.split("-").find((text) => text.includes('enable'));
            if (item.keyName === undefined) {
              return obj;
            }
            else return Object.assign(obj, { [item.keyName!]: item.keyValue === 'true' ? true : false });
          }
          , {}
        );
        AppStore.userProfileStore.applicationFlags = reducedValues as IApplicationFlags;
        if (AppStore.userProfileStore.applicationFlags.enabledemoncloud) this.swapDemUrls();
        if (AppStore.userProfileStore.applicationFlags.enableNAIPoncloud) this.swapNaipUrls();
      }
    }
    catch (error) {
      console.error(error);
    }
  }

  private async getAllFlags() {
    try {
      const response: AxiosResponse<FlagDto> = await PRJAPI.FfsController.getAllFlagsUsingGET();
      if (response.data && response.data.result) {
        const reducedValues = response.data.result.reduce(
          (obj, item) => Object.assign(obj, { [item.keyName!]: item.keyValue === 'true' ? true : false }),
          {}
        );
        AppStore.userProfileStore.applicationFlags = reducedValues as IApplicationFlags;
        if (AppStore.userProfileStore.applicationFlags.enableclippeddem === undefined) AppStore.userProfileStore.applicationFlags.enableclippeddem = true;// temporary until backend gets the flag in our system
        if (AppStore.userProfileStore.applicationFlags.enabledemoncloud) this.swapDemUrls();
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
      return;
    }
  }
  private swapDemUrls() {
    appConfig.demMapServices.forEach((dem, index) => {
      dem.url = dem.url.replace('https://gis.sc.egov.usda.gov/image/', 'https://intranet.geo.fpac.usda.gov/nrcs-elevation/');
      dem.metadata = dem.metadata.replace('https://gis.sc.egov.usda.gov/data/', 'https://intranet.geo.fpac.usda.gov/nrcs-geodata/');
    })
  }
  private swapNaipUrls() {
    // Temp solution to swap NAIP urls to cloud urls. Once switch is complete, this can be removed 
    // and the cloud urls can be the default in the appConfig
    appConfig.usdaNaipSources.baseMapPath =
      'https://apps.geo.fpac.usda.gov/geo-imagery/rest/services/naip/conus_naip/ImageServer';
    appConfig.usdaNaipSources.nonTiledPath =
      'https://apps.geo.fpac.usda.gov/geo-imagery/rest/services/naip/conus_naip/ImageServer';
  }
}
